import { Trash2 } from 'lucide-react';
import React from 'react';
import type { FieldInfo } from '@reach/shared-types/segment-schema-graph';
import { AddDescriptionButton } from './AddDescriptionButton';
import { ClauseDescriptionHeadline } from './ClauseDescriptionHeadline';
import { acceptsMultipleValues, getOperatorsForFieldType } from './field-type-operators';
import { FieldSelector } from './FieldSelector';
import { OperatorSelector } from './OperatorSelector';
import { SchemaAttributeBadge } from './SchemaAttributeBadge';
import type { BuilderFieldCondition, FieldConditionSubject } from './segment-builder.types';
import styles from './SegmentVisualBuilder.module.css';
import { useClauseDescriptionVisible, useSegmentReadOnly } from './segment-viewer-settings-context';
import { ValueInput } from './ValueInput';
import { isDateValueCompatibleWithOperator } from './ValueInput/date-value-utils';

interface FieldConditionRowProps {
  condition: BuilderFieldCondition;
  fieldOptions: FieldInfo[];
  subject: FieldConditionSubject;
  onUpdate: (condition: BuilderFieldCondition) => void;
  onDelete: () => void;
  disabled?: boolean;
  /** Inside a related-record block or sub group, below the condition group's direct clauses. */
  nested?: boolean;
}

function getValueForOperatorChange(
  condition: BuilderFieldCondition,
  operator: BuilderFieldCondition['operator'],
): BuilderFieldCondition['value'] {
  if (condition.value.length === 0) return condition.value;

  if (condition.fieldType === 'date') {
    const firstValue = condition.value[0];
    return isDateValueCompatibleWithOperator(operator, firstValue) ? condition.value.slice(0, 1) : [];
  }

  // Single-value inputs edit value[0]; keeping the rest would silently widen the compiled OR
  return acceptsMultipleValues(operator, condition.fieldType)
    ? condition.value
    : condition.value.slice(0, 1);
}

export const FieldConditionRow: React.FC<FieldConditionRowProps> = ({
  condition,
  fieldOptions,
  subject,
  onUpdate,
  onDelete,
  disabled = false,
  nested = false,
}) => {
  const readOnly = useSegmentReadOnly();
  const descriptionVisible = useClauseDescriptionVisible(nested);
  const canAddDescription = descriptionVisible && !readOnly && condition.description === undefined;
  const visibleDescription = descriptionVisible ? condition.description : undefined;
  const currentFieldInfo = fieldOptions.find(f => f.name === condition.field);

  const handleFieldChange = (field: string) => {
    const option = fieldOptions.find(f => f.name === field);
    const fieldType = option?.type ?? 'unknown';
    const fieldTypeChanged = fieldType !== condition.fieldType;
    const operators = getOperatorsForFieldType(fieldType);
    const operator = operators.includes(condition.operator) ? condition.operator : operators[0];
    onUpdate({
      ...condition,
      field,
      fieldType,
      operator,
      value: fieldTypeChanged ? [] : condition.value,
    });
  };

  const handleOperatorChange = (operator: BuilderFieldCondition['operator']) => {
    onUpdate({
      ...condition,
      operator,
      value: getValueForOperatorChange(condition, operator),
    });
  };

  const handleValueChange = (value: BuilderFieldCondition['value']) => {
    onUpdate({ ...condition, value });
  };

  const handleDescriptionChange = (description: string | undefined) => {
    onUpdate({ ...condition, description });
  };

  return (
    <div className={styles.conditionRowWrapper}>
      {visibleDescription !== undefined && (
        <div className={styles.rowDescription}>
          <ClauseDescriptionHeadline
            variant="row"
            description={visibleDescription}
            onChange={handleDescriptionChange}
            disabled={disabled}
          />
        </div>
      )}
      <div className={styles.conditionRow}>
        <div className={styles.conditionRowField}>
          <FieldSelector
            options={fieldOptions}
            value={condition.field}
            onChange={handleFieldChange}
            placeholder="Field"
            disabled={disabled}
            leadingIcon={<SchemaAttributeBadge subject={subject} />}
          />
        </div>
        <div className={styles.conditionRowOperator}>
          <OperatorSelector
            fieldType={condition.fieldType}
            value={condition.operator}
            onChange={handleOperatorChange}
            disabled={disabled}
            valueCount={condition.value.length}
          />
        </div>
        <div className={styles.conditionRowValue}>
          <ValueInput
            fieldType={condition.fieldType}
            operator={condition.operator}
            value={condition.value}
            onChange={handleValueChange}
            disabled={disabled}
            enumOptions={currentFieldInfo?.enum}
            schemaId={subject.schemaId}
            field={condition.field}
          />
        </div>
        <div className={styles.conditionRowControls}>
          {canAddDescription && (
            <AddDescriptionButton onClick={() => handleDescriptionChange('')} disabled={disabled} />
          )}
          {!readOnly && (
            <button
              type="button"
              className={styles.conditionRowDelete}
              onClick={onDelete}
              disabled={disabled}
              aria-label="Remove condition">
              <Trash2 size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
